import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { usePipeline, PIPELINES } from "../lib/pipeline";
import {
  ChartLineUp, UsersThree, FlowArrow, Megaphone, Robot, MapTrifold,
  ChatCircleDots, ListChecks, Clock, Gear, SignOut, Trophy, Compass, Brain,
  Note, BookOpen, CalendarBlank, Bell, UserGear, ListNumbers, Plugs,
  Stethoscope, Lightbulb, ClockCounterClockwise, PresentationChart, Warning, Headset, Scroll,
} from "@phosphor-icons/react";

const SUP_ROLES = ["super_admin", "hm_admin", "community_analyst"];
const HA_ROLES = ["super_admin", "hm_admin", "community_analyst", "campaign_operator"];

const NAV_GROUPS = [
  {
    label: "Visión",
    items: [
      { to: "/", label: "Dashboard", icon: ChartLineUp, end: true, testid: "nav-dashboard" },
      { to: "/intelligence", label: "AI Intelligence", icon: Brain, testid: "nav-intelligence" },
    ],
  },
  {
    label: "Comunidad",
    items: [
      { to: "/users", label: "Usuarios", icon: UsersThree, testid: "nav-users" },
      { to: "/founder", label: "Founder Pipeline", icon: Trophy, testid: "nav-founder" },
      { to: "/founder/dynamics-sequence", label: "Secuencia de Dinámicas", icon: ListNumbers, testid: "nav-dynamics-sequence" },
      { to: "/ambassador", label: "Ambassador Pipeline", icon: Compass, testid: "nav-ambassador" },
      { to: "/territories", label: "Territorios", icon: MapTrifold, testid: "nav-territories" },
      { to: "/legacy", label: "Cohorte Legacy", icon: ClockCounterClockwise, testid: "nav-legacy" },
    ],
  },
  {
    label: "Motor conversacional",
    items: [
      { to: "/pipelines", label: "Editor de pipelines", icon: FlowArrow, testid: "nav-pipelines" },
      { to: "/agents", label: "Prompts & agentes", icon: Robot, testid: "nav-agents" },
      { to: "/templates", label: "Templates WA", icon: Note, testid: "nav-templates" },
      { to: "/campaigns", label: "Campañas", icon: Megaphone, testid: "nav-campaigns" },
    ],
  },
  {
    label: "Operación",
    items: [
      { to: "/whatsapp", label: "Simulador WhatsApp", icon: ChatCircleDots, testid: "nav-whatsapp" },
      { to: "/inbox", label: "Bandeja", icon: ChatCircleDots, testid: "nav-inbox" },
      { to: "/calendar", label: "Calendario", icon: CalendarBlank, testid: "nav-calendar", badgeKey: "total" },
      { to: "/reminders", label: "Recordatorios", icon: Bell, testid: "nav-reminders" },
      { to: "/ai-logs", label: "Auditoría IA", icon: ListChecks, testid: "nav-ailogs" },
      { to: "/scheduler", label: "Scheduler", icon: Clock, testid: "nav-scheduler" },
    ],
  },
  {
    label: "Inteligencia Conversacional",
    items: [
      { to: "/supervisor", label: "Dashboard Supervisor", icon: PresentationChart, end: true, testid: "nav-sup-dashboard", roles: SUP_ROLES },
      { to: "/supervisor/diagnoses", label: "Diagnósticos", icon: Stethoscope, testid: "nav-sup-diagnoses", roles: SUP_ROLES },
      { to: "/supervisor/recommendations", label: "Recomendaciones", icon: Lightbulb, testid: "nav-sup-recommendations", roles: SUP_ROLES },
      { to: "/supervisor/versions", label: "Versiones", icon: ClockCounterClockwise, testid: "nav-sup-versions", roles: SUP_ROLES },
      { to: "/supervisor/escalations", label: "Escalamientos", icon: Warning, testid: "nav-sup-escalations", roles: SUP_ROLES },
      { to: "/supervisor/inbox", label: "Bandeja Humana", icon: Headset, testid: "nav-sup-inbox", roles: HA_ROLES, badgeKey: "human_new" },
      { to: "/supervisor/audit", label: "Auditoría", icon: Scroll, testid: "nav-sup-audit", roles: SUP_ROLES },
    ],
  },
  {
    label: "Sistema",
    items: [
      { to: "/manual", label: "Manual de uso", icon: BookOpen, testid: "nav-manual" },
      { to: "/settings/whatsapp", label: "Números WhatsApp", icon: ChatCircleDots, testid: "nav-wa-numbers" },
      { to: "/settings/sources", label: "Fuentes de leads", icon: FlowArrow, testid: "nav-sources" },
      { to: "/settings/odoo", label: "Test Odoo", icon: Plugs, testid: "nav-odoo", adminOnly: true },
      { to: "/settings/team", label: "Equipo · Operadores", icon: UserGear, testid: "nav-team", adminOnly: true },
      { to: "/settings", label: "Configuración", icon: Gear, testid: "nav-settings" },
    ],
  },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { pipelines, activePipelineSlug, setActivePipelineSlug } = usePipeline();
  const navigate = useNavigate();
  const [badges, setBadges] = useState({});

  useEffect(() => {
    let cancelled = false;
    const fetchBadges = async () => {
      try {
        const { data } = await api.get("/calendar/pending-count");
        let merged = data || {};
        try {
          const { data: hi } = await api.get("/human-inbox/pending-count");
          merged = { ...merged, human_new: hi?.new || 0 };
        } catch { /* rol sin acceso a bandeja */ }
        if (!cancelled) setBadges(merged);
      } catch { /* */ }
    };
    fetchBadges();
    const t = setInterval(fetchBadges, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-page">
      {/* Sidebar */}
      <aside className="w-64 bg-carbon-900 text-carbon-200 flex flex-col fixed h-screen" data-testid="sidebar">
        <div className="px-6 py-7 border-b border-carbon-800">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-terracotta rounded flex items-center justify-center font-heading font-bold text-white">
              HM
            </div>
            <div>
              <div className="font-heading text-base font-semibold text-white leading-tight">
                Heroes Migrantes
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-carbon-400">
                Community Engine
              </div>
            </div>
          </div>
        </div>

        {/* Pipeline Switcher · scope global de toda la app */}
        <div className="px-3 py-3 border-b border-carbon-800" data-testid="pipeline-switcher">
          <div className="text-[9px] uppercase tracking-[0.18em] text-carbon-500 font-medium mb-1.5 px-1">
            Pipeline activo
          </div>
          <div className="space-y-0.5">
            {(pipelines || PIPELINES).map((p) => {
              const active = activePipelineSlug === p.slug;
              const colorBg = active
                ? p.slug === "founder"
                  ? "bg-terracotta/15 border-terracotta/40 text-white"
                  : p.slug === "ambassador"
                    ? "bg-forest/15 border-forest/40 text-white"
                    : "bg-carbon-700 border-carbon-600 text-white"
                : "border-transparent text-carbon-400 hover:text-carbon-200 hover:bg-carbon-800";
              return (
                <button
                  key={p.slug}
                  onClick={() => setActivePipelineSlug(p.slug)}
                  data-testid={`pipeline-btn-${p.slug}`}
                  className={`w-full text-left px-3 py-1.5 rounded text-xs flex items-center gap-2 border ${colorBg} transition-colors`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    p.slug === "founder" ? "bg-terracotta" :
                    p.slug === "ambassador" ? "bg-forest" : "bg-carbon-400"
                  }`} />
                  {p.short}
                </button>
              );
            })}
          </div>
        </div>

        <nav className="flex-1 py-4 px-3 overflow-y-auto scrollbar-thin">
          {NAV_GROUPS.map((group) => {
            if (group.roles && !group.roles.includes(user?.role)) return null;
            const visibleItems = group.items.filter((it) => {
              if (it.adminOnly && !["super_admin", "hm_admin"].includes(user?.role)) return false;
              if (it.roles && !it.roles.includes(user?.role)) return false;
              return true;
            });
            if (visibleItems.length === 0) return null;
            return (
            <div key={group.label} className="mb-4">
              <div className="px-3 mb-1 text-[10px] uppercase tracking-[0.18em] text-carbon-500 font-medium">
                {group.label}
              </div>
              {visibleItems.map(({ to, label, icon: Icon, end, testid, badgeKey }) => {
                const badgeValue = badgeKey ? badges?.[badgeKey] : 0;
                return (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    data-testid={testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 mb-0.5 rounded text-sm transition-colors relative ${
                        isActive
                          ? "bg-white/10 text-white border-l-2 border-terracotta pl-[10px]"
                          : "text-carbon-300 hover:text-white hover:bg-white/5"
                      }`
                    }
                  >
                    <Icon size={17} weight="regular" />
                    <span>{label}</span>
                    {badgeValue > 0 && (
                      <span data-testid={`${testid}-badge`} className="ml-auto bg-terracotta text-white text-[10px] font-mono px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
                        {badgeValue}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-carbon-800">
          <div className="px-3 py-2 mb-1">
            <div className="text-xs text-carbon-400">Sesión</div>
            <div className="text-sm text-white font-medium truncate" data-testid="user-email">
              {user?.email}
            </div>
            <div className="text-[10px] uppercase tracking-wider text-terracotta">
              {user?.role?.replace("_", " ")}
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-sm text-carbon-300 hover:text-white hover:bg-white/5 transition-colors"
          >
            <SignOut size={16} />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 ml-64 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}
